import { Button } from "@/components/ui/Button";
import {
  newOnlineBookingSeason,
  type OnlineBookingSeasonPeriod,
} from "@/lib/onlineBooking";

type Props = {
  seasons: OnlineBookingSeasonPeriod[];
  onChange: (seasons: OnlineBookingSeasonPeriod[]) => void;
  canWrite: boolean;
  idPrefix?: string;
};

export function OnlineBookingSeasonsEditor({
  seasons,
  onChange,
  canWrite,
  idPrefix = "season",
}: Props) {
  const updateSeason = (
    seasonId: string,
    updater: (season: OnlineBookingSeasonPeriod) => OnlineBookingSeasonPeriod,
  ) => {
    onChange(seasons.map((season) => (season.id === seasonId ? updater(season) : season)));
  };

  return (
    <fieldset className="space-y-4" disabled={!canWrite}>
      {seasons.length === 0 ? (
        <p className="text-sm text-label-secondary">Keine Saisons definiert.</p>
      ) : (
        <ul className="space-y-4">
          {seasons.map((season) => (
            <li key={season.id} className="space-y-3 rounded-lg border border-separator p-3">
              <div className="flex flex-wrap items-end gap-3">
                <label className="flex min-w-[12rem] flex-1 flex-col gap-1 text-sm">
                  Bezeichnung
                  <input
                    type="text"
                    className="eh-input"
                    value={season.name}
                    onChange={(e) =>
                      updateSeason(season.id, (current) => ({ ...current, name: e.target.value }))
                    }
                    placeholder="z. B. Hauptsaison"
                  />
                </label>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => onChange(seasons.filter((item) => item.id !== season.id))}
                >
                  Entfernen
                </Button>
              </div>
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium text-label">Zeitraum-Typ</legend>
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="radio"
                    name={`${idPrefix}-mode-${season.id}`}
                    checked={season.mode === "recurring"}
                    onChange={() =>
                      updateSeason(season.id, (current) => ({
                        ...newOnlineBookingSeason("recurring"),
                        id: current.id,
                        name: current.name,
                      }))
                    }
                  />
                  <span>
                    <strong>Jährlich wiederkehrend</strong> – z. B. 01.04. bis 30.09. jedes Jahr
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="radio"
                    name={`${idPrefix}-mode-${season.id}`}
                    checked={season.mode === "fixed"}
                    onChange={() =>
                      updateSeason(season.id, (current) => ({
                        ...newOnlineBookingSeason("fixed"),
                        id: current.id,
                        name: current.name,
                      }))
                    }
                  />
                  <span>
                    <strong>Festes Kalenderjahr</strong> – exakte Start- und Enddaten
                  </span>
                </label>
              </fieldset>
              {season.mode === "recurring" ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="flex flex-col gap-1 text-sm">
                    Von (Tag.Monat)
                    <div className="flex gap-2">
                      <input
                        type="number"
                        className="eh-input w-20"
                        min={1}
                        max={31}
                        value={season.start_day ?? ""}
                        onChange={(e) =>
                          updateSeason(season.id, (current) => ({
                            ...current,
                            start_day: Number(e.target.value) || null,
                          }))
                        }
                        aria-label="Starttag"
                      />
                      <input
                        type="number"
                        className="eh-input w-20"
                        min={1}
                        max={12}
                        value={season.start_month ?? ""}
                        onChange={(e) =>
                          updateSeason(season.id, (current) => ({
                            ...current,
                            start_month: Number(e.target.value) || null,
                          }))
                        }
                        aria-label="Startmonat"
                      />
                    </div>
                  </label>
                  <label className="flex flex-col gap-1 text-sm">
                    Bis (Tag.Monat)
                    <span className="text-xs text-label-secondary">
                      Letzter offener Tag, nur Abreise
                    </span>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        className="eh-input w-20"
                        min={1}
                        max={31}
                        value={season.end_day ?? ""}
                        onChange={(e) =>
                          updateSeason(season.id, (current) => ({
                            ...current,
                            end_day: Number(e.target.value) || null,
                          }))
                        }
                        aria-label="Endtag"
                      />
                      <input
                        type="number"
                        className="eh-input w-20"
                        min={1}
                        max={12}
                        value={season.end_month ?? ""}
                        onChange={(e) =>
                          updateSeason(season.id, (current) => ({
                            ...current,
                            end_month: Number(e.target.value) || null,
                          }))
                        }
                        aria-label="Endmonat"
                      />
                    </div>
                  </label>
                </div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="flex flex-col gap-1 text-sm">
                    Von
                    <input
                      type="date"
                      className="eh-input"
                      value={season.start_date ?? ""}
                      onChange={(e) =>
                        updateSeason(season.id, (current) => ({
                          ...current,
                          start_date: e.target.value || null,
                        }))
                      }
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-sm">
                    Bis
                    <span className="text-xs text-label-secondary">
                      Letzter offener Tag, nur Abreise
                    </span>
                    <input
                      type="date"
                      className="eh-input"
                      value={season.end_date ?? ""}
                      onChange={(e) =>
                        updateSeason(season.id, (current) => ({
                          ...current,
                          end_date: e.target.value || null,
                        }))
                      }
                    />
                  </label>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="secondary"
          onClick={() => onChange([...seasons, newOnlineBookingSeason("recurring")])}
        >
          Jährliche Saison hinzufügen
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={() => onChange([...seasons, newOnlineBookingSeason("fixed")])}
        >
          Saison mit festem Jahr hinzufügen
        </Button>
      </div>
    </fieldset>
  );
}
